/**
 * @Author: v_jppjpeng
 * @Date: 2023-07-11 11:51:00
 * @Explain: 带分页器的table
 */
import { intlGet } from '@/locales';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination, PrimaryTableCol, Table, TableProps } from 'tdesign-react';
// utils
import { isFunction } from '@/utils/common/dataJudgment';
import { millimeterFormat } from '@/utils/format/number';
// type
import { StatusTipEnum } from '@/definition/common/data.d';
import { DefaultPageStatusEnum } from '../data.d';
// components
import DefaultPage from '../defaultPage';
import Icon from '../icon';

type PropsType = {
  className?: string;
  /** table - props */
  tableProps?: TableProps;
  /** 表格唯一标识 */
  rowKey?: TableProps['rowKey'];
  /** 是否显示表格边框*/
  bordered?: TableProps['bordered'];
  /** 表头 */
  columns?: Array<PrimaryTableCol<any>>;
  /** 数据 */
  records?: any[];
  /** 数据加载状态 */
  reqStatus?: string;
  /** 是否展示总数 */
  isShowTotalContent?: boolean;
  /** 是否展示分页 */
  isShowPaging?: boolean;
  /** 分页左侧区域 */
  leftContent?: ReactNode;
  /** 总数 */
  recordCount?: number;
  /** 搜索参数 */
  searchParams?: {
    pageIndex?: number;
    pageSize?: number;
  };
  /** 搜索类型 - 默认为default，interval当为定时刷新时，则不展示loading  */
  searchType?: 'default' | 'interval' | string;
  /** 表格需要减少多少高度才能适应满屏 */
  reduceHeight?: number;
  customScrollableHeight?: number;
  /** 是否刷新 */
  setIsSearch?: (val: boolean) => void;
  /** 设置搜索参数 */
  setSearchValue?: (data: any) => void;
  onPagingChange?: (data: { pageIndex: number; pageSize: number }) => void;
};

export default function TdTable(props: PropsType) {
  const {
    className,
    tableProps,
    rowKey = 'id',
    bordered = false,
    columns,
    records = [],
    reqStatus,
    isShowTotalContent = true,
    isShowPaging = true,
    leftContent,
    recordCount = 0,
    searchParams,
    searchType,
    reduceHeight = 76 + 24 + 72 + 32 + 48 + 43 + 50 + 12 + 6,
    customScrollableHeight = 0,
    setIsSearch,
    setSearchValue,
    onPagingChange,
  } = props;

  const [scrollableHeight, setScrollableHeight] = useState(
    customScrollableHeight,
  );

  const statusTip = useMemo(() => {
    switch (reqStatus) {
      case StatusTipEnum.empty:
        return <DefaultPage status={DefaultPageStatusEnum.NO_DATA} />;
      case StatusTipEnum.searchEmpty:
        return <DefaultPage status={DefaultPageStatusEnum.SEARCH_NO_DATA} />;
      case StatusTipEnum.error:
        return (
          <DefaultPage
            status={DefaultPageStatusEnum.NO_NETWORK}
            onRetry={() => setIsSearch?.(true)}
          />
        );
      case StatusTipEnum.loading:
        return searchType === 'interval' ? (
          ''
        ) : (
          <DefaultPage status={DefaultPageStatusEnum.LOADING} />
        );
      default:
        return '';
    }
  }, [reqStatus]);

  const onChange = (pageInfo: {
    current: number;
    previous: number;
    pageSize: number;
  }) => {
    const params = {
      pageIndex: pageInfo.current,
      pageSize: pageInfo.pageSize,
    };
    if (onPagingChange && isFunction(onPagingChange)) {
      onPagingChange?.(params);
    } else {
      setSearchValue?.(params);
      setIsSearch?.(true);
    }
  };

  // 数据总数
  const dataCount = useMemo(
    () => records?.length || tableProps?.data?.length,
    [records, tableProps?.data],
  );

  const pageIndex: number = useMemo(
    () => searchParams?.pageIndex || 1,
    [searchParams?.pageIndex],
  );
  const pageSize: number = useMemo(
    () => searchParams?.pageSize || 100,
    [searchParams?.pageSize],
  );

  const pageSizeOptions = useMemo(() => {
    const arr = [10, 20, 30, 50, 100];
    return arr.map((item) => ({
      label: String(item),
      value: item,
    }));
  }, []);

  // 响应式
  const handleResize = useCallback(() => {
    if (!customScrollableHeight) {
      const maxHeight = window.innerHeight - reduceHeight;
      setScrollableHeight(maxHeight);
    }

    if (customScrollableHeight) setScrollableHeight(customScrollableHeight);
  }, [reduceHeight, customScrollableHeight]);

  useEffect(() => {
    handleResize();
    // 监听
    window.addEventListener('resize', handleResize);
    // 销毁
    return () => window.removeEventListener('resize', handleResize);
  });

  return (
    <div className={classNames('default-td-table', className)}>
      <Table
        rowKey={rowKey}
        tableLayout="fixed"
        bordered={bordered}
        maxHeight={scrollableHeight}
        // 关闭插件loading
        loadingProps={{ indicator: false }}
        // loading={
        //   searchType === 'interval' ? (
        //     ''
        //   ) : reqStatus === StatusTipEnum.loading ? (
        //     <DefaultPage status={DefaultPageStatusEnum.LOADING}/>
        //   ) : (
        //     ''
        //   )
        // }
        // 加载中，空数据, 请求错误
        empty={statusTip}
        // 表头
        columns={columns?.map((item) => {
          if (!item.cell) {
            return {
              ...item,
              cell: ({ row }: any) => (item.colKey && row[item.colKey]) || '-',
            };
          }
          return item;
        })}
        // 数据
        data={records}
        // 过滤提示
        filterRow={
          <span>
            {intlGet('COMMON.QUERY.NUMBER.RESULT', {
              num: recordCount || dataCount,
            })}
          </span>
        }
        // 过滤图标
        filterIcon={<Icon.Font name="filtrate" />}
        {...tableProps}
      />
      {reqStatus !== StatusTipEnum.loading &&
      (isShowTotalContent || isShowPaging) ? (
        <Pagination
          className="pt-20 safari-pagination"
          pageSizeOptions={pageSizeOptions}
          total={recordCount || dataCount}
          totalContent={
            isShowTotalContent
              ? leftContent ||
                intlGet('COMMON.RECORD.COUNT', {
                  recordCount: millimeterFormat(recordCount || dataCount),
                })
              : ''
          }
          current={pageIndex}
          pageSize={pageSize}
          showPageSize={isShowPaging}
          showFirstAndLastPageBtn={isShowPaging}
          showPreviousAndNextBtn={isShowPaging}
          showPageNumber={isShowPaging}
          onChange={onChange}
        />
      ) : (
        ''
      )}
    </div>
  );
}
